import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { validateStep } from '../Validators';

import { FadeIn } from 'hosman-material';
import NavigationButtons from '../../components/NavigationButtons'
import AddressInput from './components/AddressInput';
import TextInput from './components/TextInput';
import { updateStep, updateEstimationData, updateErrors, updateDisplayStreetNumberError } from '../../../../../../reducers/_Pages/Public/estimationFunnelSlice';
import { initWoosmapScript, extractAddress } from '../../../../../../../shared/utils/mapService';

const Address = () => {
  const { estimationData, estimationData: { placeData: { formatted_address }}, errors, displayStreetNumberError } = useSelector((state) => state.estimationFunnel);
  const [triggerValidation, setTriggerValidation] = useState(false);
  const [streetNumber, setStreetNumber] = useState(null);
  const streetNumberDebounce = useRef(null);
  const streetNumberService = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    const contract = validateStep({ stepNumber: 5, data: estimationData });

    if (!contract.valid) {
      dispatch(updateStep({ stepNumber: 1 }))
    }
  }, []);

  useEffect(() => {
    if (!triggerValidation) return

    const contract = validateStep({ stepNumber: 6, data: estimationData });
    if (contract.valid) {
      dispatch(updateStep({ stepNumber: 7 }))
    } else {
      toast.error('Le numéro de rue est requis', { id: 'address-validation-fail', duration: 3000 })
      dispatch(updateErrors(contract.errors))
      dispatch(updateDisplayStreetNumberError(true))
    }
  }, [triggerValidation]);

  const handleChange = (addressObj) => {
    dispatch(updateErrors({}))
    setTriggerValidation(false)
    dispatch(updateEstimationData({ ...estimationData, placeData: addressObj }))

    if (addressObj.lat) {
      setTriggerValidation(true)
    } else {
      dispatch(updateDisplayStreetNumberError(false))
    }
  }

  const handleChangeStreetNumber = (e) => {
    dispatch(updateErrors({}));
    setStreetNumber(e.target.value);
    setTriggerValidation(false);

    const number = e.target.value;
    clearTimeout(streetNumberDebounce.current);
    if (!number || number.length < 1) return;

    streetNumberDebounce.current = setTimeout(async () => {
      try {
        const searchTerm = estimationData.placeData.street_name
          ? `${number} ${estimationData.placeData.street_name}, ${estimationData.placeData.city}`
          : `${number} ${estimationData.placeData.formatted_address}`;

        await initWoosmapScript();
        if (!streetNumberService.current) streetNumberService.current = new woosmap.map.LocalitiesService();

        // 1. Autocomplete silencieux
        const autocompleteResponse = await streetNumberService.current.autocomplete({
          input: searchTerm,
          types: ['address'],
          language: 'FR',
          components: { country: ['fr-fr'] },
        });

        const firstPrediction = autocompleteResponse.localities[0];
        if (!firstPrediction) return;

        // 2. getDetails avec le public_id de la première prédiction
        const detailsResponse = await streetNumberService.current.getDetails({ publicId: firstPrediction.public_id });
        const addressObj = extractAddress('formatted_address', detailsResponse.result);

        if (!addressObj) return;

        dispatch(updateEstimationData({ ...estimationData, placeData: addressObj }));
      } catch (error) {
        toast.error("Une erreur est survenue lors de la récupération de l'adresse");
      }
    }, 250);
  };

  return (
    <div data-testid='Address'>
      <FadeIn>
        <div className='flex flex-col justify-center items-center pt-14'>
          <div className='flex flex-col items-center justify-center gap-7'>
            <div className='flex flex-col gap-7'>
              <div className='h2 text-ih-indigo text-left'>
                <p className='w-full md:w-3/4'>Quelle est l'adresse du bien à estimer ?</p>
              </div>

              <div className='flex flex-col gap-4 w-full'>
                <AddressInput
                  name='formatted_address'
                  onChange={handleChange}
                  value={formatted_address}
                  placeholder="Saisir l'adresse du bien ici..."
                  listPlacement='bottom-auto relative'
                  errors={errors}
                />

                <p className='text-ih-indigo-60 text-sm'>Ex : 12 rue Saint Denis, 75011 Paris</p>

                <div className={`transition-all duration-300 ease-in-out ${
                  displayStreetNumberError ? 'opacity-100 h-fit' : 'opacity-0 max-h-0'
                } overflow-hidden`}
                >
                  <TextInput
                    name='street_number'
                    label='Numéro de rue'
                    onChange={handleChangeStreetNumber}
                    value={streetNumber || ''}
                    placeholder='12'
                    listPlacement='bottom-auto relative'
                    errors={errors}
                    inputMode='numeric'
                    pattern='[0-9]*'
                  />
                </div>
              </div>
            </div>

            <NavigationButtons classes='hidden md:block w-full my-5' />
          </div>
        </div>
      </FadeIn>
    </div>
  )
}

export default Address;
